import { Injectable, DOCUMENT, inject } from '@angular/core'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Locale, Locales } from '@wm-front/shared/interfaces'
import { getWmLocale } from '../helpers/get-wm-locale'

declare const $localize: any

@Injectable()
export class I18nService {
  /** Default language of the app */
  public defaultLanguage!: string

  /** Its supported languages */
  public supportedLanguages!: string[]

  /** The language options available in the dropdown */
  public supportedLanguageOptions: {
    value: string
    label: string
    checked: boolean
  }[] = []

  protected _document = inject<Document>(DOCUMENT)

  /** locale variable to store the angular locale */
  private _locale: string

  /** The window object */
  private window: Window

  /** Contain iso and timezone for FR, GB and SG */
  private _locales: Locales = {
    GB: { iso: 'en-GB', timeZone: 'Europe/London' },
    FR: { iso: 'fr-FR', timeZone: 'Europe/Paris' },
    SG: { iso: 'en-GB', timeZone: 'Asia/Singapore' },
    HK: { iso: 'en-GB', timeZone: 'Asia/Hong_Kong' },
    PT: { iso: 'pt-PT', timeZone: 'Europe/Lisbon' },
  }

  constructor() {
    const _document = this._document

    this.window = _document.defaultView as Window
  }

  /** Gets the language without its locale */
  public get languageWithoutLocale(): string {
    return this.language.split('-')[0]
  }

  /**
   * Return the current language translated.
   * @return the current language translated
   */
  public get currentLanguageName(): string | undefined {
    return this.supportedLanguageOptions.find(
      ({ value }) => value === this.language
    )?.label
  }

  /**
   * The locales managed in the app
   * @returns {Locales}
   */
  public get locales(): Locales {
    return this._locales
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  public get language(): string {
    return this._locale
  }

  /**
   * Sets the current language.
   * @param { string } language The IETF language code to set.
   */
  private set language(language: string) {
    let isSupportedLanguage = this.supportedLanguages.includes(language)

    /**  If no exact match is found, search without the region **/
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0]
      language =
        this.supportedLanguages.find((supportedLanguage) =>
          supportedLanguage.startsWith(language)
        ) || ''
      isSupportedLanguage = Boolean(language)
    }

    /**  Fallback if language is not supported **/
    if (!isSupportedLanguage) {
      language = this.defaultLanguage
    }

    this._locale = language

    console.log(`Language set to ${language}`)
  }

  /**
   * Initializes i18n for the application.
   * @param { string } defaultLanguage The default language to use.
   * @param { string[] } supportedLanguages The list of supported languages.
   */
  public init(
    defaultLanguage: string,
    supportedLanguages: { locale: string; name: string }[]
  ): void {
    this.defaultLanguage = defaultLanguage
    this.supportedLanguages = supportedLanguages.map((lang) => lang.locale)

    this.language = localStorage.getItem('wm-locale')
      ? (localStorage.getItem('wm-locale') as string)
      : getWmLocale()

    this.supportedLanguageOptions = supportedLanguages.map(
      ({ locale, name }) => ({
        value: locale,
        label: name,
        checked: locale === this.language,
      })
    )
  }

  /** Method to be called throughout the app
   * @param { string } locale the locale to be set
   */
  public setLocale(locale: string): void {
    /** Set the locale */
    this.language = locale

    /** Update it in storage */
    localStorage.setItem('wm-locale', locale)

    /** Reload the page */
    this.window.location.reload()
  }

  /**
   * Format the number to the current language
   * @param {number} count
   * @return {string}
   */
  public formatNumber(count: number): string {
    return new Intl.NumberFormat(this.language).format(count)
  }

  /**
   * Return the lang associated to the territory
   * @param {string} territory
   * @returns {string}
   */
  public getLangFromTerritory(territory: string): Locale {
    return this.locales[territory]
  }

  /**
   * Translate for the appropriate localization
   * @param { string } context context for your translators
   * @param { string } definition translation description
   * @param { string } id custom translation identifiers
   * @param { translate } translate translation for the default localization
   */
  // public translate(
  //   context: string,
  //   definition: string,
  //   id: string,
  //   translate: string
  // ): string {
  //   return $localize({
  //     '0': `:${context} | ${definition}@@${id}:${translate}`,
  //     raw: [':'],
  //   } as any)
  // }
}
